import {toast} from 'react-toastify';
import BusinessRecordStore from '../store/business-record-store';
import {BusinessRecordDto} from '../../entity/dto/business-record-dto';
import {CompanyType, getCompanyTypeText} from '../../entity/enums/company-type';
import {State} from '../../entity/model/state';
import {County} from '../../entity/model/county';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

export default class BusinessRecordService {
  constructor(private businessRecordStore: BusinessRecordStore) {}

  getBusinessRecordDto = (): BusinessRecordDto => {
    return {
      companyName: '',
      companyType: 'all',
      contactPerson: '',
      contactEmail: '',
      phone: '',
      website: '',
      yearsInBusiness: 0,
      isNationwide: false,
      locations: []
    };
  }

  createBusinessRecords = async (businessRecords: BusinessRecordDto[], states: State[], counties: County[]): Promise<boolean> => {
    if (businessRecords.length === 0) {
      toast.warning('Please provide business record data.');
      return false;
    }

    if (states.length === 0) {
      toast.warning('States data not loaded. Please refresh the page.');
      return false;
    }

    const groupedData = new Map<string, BusinessRecordDto>();

    for (const row of businessRecords) {
      const companyKey = `${row.companyName}|${row.companyType}|${row.contactPerson}|${row.contactEmail}|${row.phone}|${row.website}|${row.isNationwide}|${row.yearsInBusiness}`;
      
      if (groupedData.has(companyKey)) {
        const existingCompany = groupedData.get(companyKey)!;
        if (!row.isNationwide && row.locations.length > 0)
          existingCompany.locations.push(...row.locations);
      } else {
        const businessRecordDto: BusinessRecordDto = {
          companyName: row.companyName,
          companyType: row.companyType,
          contactPerson: row.contactPerson,
          contactEmail: row.contactEmail,
          phone: row.phone,
          website: row.website,
          yearsInBusiness: row.yearsInBusiness,
          isNationwide: row.isNationwide,
          locations: row.isNationwide ? [] : row.locations
        }
        groupedData.set(companyKey, businessRecordDto);
      }
    }

    const processedBusinessRecords = Array.from(groupedData.values());

    const response = await this.businessRecordStore.createBusinessRecords(processedBusinessRecords);

    if (typeof response === 'string') {
      toast.error(response);
      return false;
    } else {
      toast.success(`Successfully submitted ${processedBusinessRecords.length} title service providers!`);
      return true;
    }
  }

  generateTitleServiceTemplate = async (): Promise<boolean> => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Title Service Providers');

    worksheet.columns = [
      {header: 'Company Name', key: 'companyName', width: 25},
      {header: 'Company Type', key: 'companyType', width: 20},
      {header: 'Contact Person', key: 'contactPerson', width: 20},
      {header: 'Contact Email', key: 'contactEmail', width: 25},
      {header: 'Phone', key: 'phone', width: 15},
      {header: 'Website', key: 'website', width: 25},
      {header: 'Is Nationwide', key: 'isNationwide', width: 15},
      {header: 'State', key: 'stateID', width: 15},
      {header: 'Counties (Separate by Commas)', key: 'countyIDs', width: 32},
      {header: 'Years in Business', key: 'yearsInBusiness', width: 18}
    ];

    const headerRow = worksheet.getRow(1);
    headerRow.font = {bold: true};
    headerRow.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: 'FFE0E0E0'}};

    const companyTypeValues = Object.values(CompanyType).filter(value => value !== CompanyType.ALL).map(value => getCompanyTypeText(value));

    for (let row = 2; row <= 10000; row++) {
      const companyTypeCell = worksheet.getCell(`B${row}`);
      companyTypeCell.dataValidation = {
        type: 'list',
        allowBlank: false,
        formulae: [`"${companyTypeValues.join(',')}"`],
        showErrorMessage: true,
        errorTitle: 'Invalid Company Type',
        error: 'Please select a valid company type from the dropdown list only.',
        errorStyle: 'stop'
      };

      const isNationwideCell = worksheet.getCell(`G${row}`);
      isNationwideCell.dataValidation = {
        type: 'list',
        allowBlank: false,
        formulae: ['"Yes,No"'],
        showErrorMessage: true,
        errorTitle: 'Invalid Input',
        error: 'Please select Yes or No.',
        errorStyle: 'stop'
      };

      const yearsCell = worksheet.getCell(`J${row}`);
      yearsCell.dataValidation = {
        type: 'whole',
        operator: 'greaterThan',
        formulae: [0],
        showErrorMessage: true,
        errorTitle: 'Invalid Input',
        error: 'Please enter a valid number greater than 0.',
        errorStyle: 'stop'
      };
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
    saveAs(blob, 'Title_Service_Providers_Template.xlsx');

    toast.success('Template downloaded successfully!');
    return true;
  }
}
